export function asRecord(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {};
  }
  return value as Record<string, unknown>;
}

export function truncateNonEmptyText(
  value: string | null | undefined,
  maxLength: number,
) {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  return trimmed.length > maxLength ? trimmed.slice(0, maxLength) : trimmed;
}

export function sanitizeStructuredValue(value: unknown, depth = 0): unknown {
  if (value == null) return value;
  if (typeof value === "string") {
    return truncateNonEmptyText(value, 400);
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return value;
  }
  if (depth >= 3) {
    return "[truncated]";
  }
  if (Array.isArray(value)) {
    return value
      .slice(0, 12)
      .map((entry) => sanitizeStructuredValue(entry, depth + 1));
  }
  if (typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .slice(0, 24)
        .map(([key, entryValue]) => [
          key,
          sanitizeStructuredValue(entryValue, depth + 1),
        ]),
    );
  }
  return String(value);
}

export function sanitizeStructuredRecord(
  value: unknown,
): Record<string, unknown> {
  const sanitized = sanitizeStructuredValue(value);
  if (sanitized && typeof sanitized === "object" && !Array.isArray(sanitized)) {
    return sanitized as Record<string, unknown>;
  }
  return {};
}

export function firstNonEmptyString(...values: unknown[]) {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }
  return null;
}

export function firstFiniteNumber(...values: unknown[]) {
  for (const value of values) {
    if (typeof value === "number" && Number.isFinite(value)) {
      return value;
    }
  }
  return null;
}

export function firstNonEmptyStringArray(...values: unknown[]) {
  for (const value of values) {
    if (!Array.isArray(value) || value.length === 0) continue;
    const normalized = value
      .map((entry) => (typeof entry === "string" ? entry.trim() : ""))
      .filter((entry): entry is string => entry.length > 0);
    if (normalized.length > 0) {
      return normalized;
    }
  }
  return null;
}

export function describeStructuredError(
  error: unknown,
  {
    fallbackMessage,
    nameMaxLength = 120,
    codeMaxLength = 120,
    stackMaxLength = 1200,
    data,
  }: {
    fallbackMessage: string;
    nameMaxLength?: number;
    codeMaxLength?: number;
    stackMaxLength?: number;
    data?: unknown;
  },
) {
  const record = asRecord(error);
  const messageCandidate =
    error instanceof Error
      ? error.message
      : typeof error === "string"
        ? error
        : typeof record.message === "string"
          ? record.message
          : typeof record.error === "string"
            ? record.error
            : null;
  const nameCandidate =
    error instanceof Error
      ? error.name
      : typeof record.name === "string"
        ? record.name
        : null;
  const codeCandidate =
    record.code ?? record.status ?? record.statusCode ?? record.errorCode ?? null;
  const stackCandidate =
    error instanceof Error
      ? error.stack
      : typeof record.stack === "string"
        ? record.stack
        : null;
  const dataValue =
    data !== undefined ? data : record.data ?? record.details ?? record.response ?? null;

  return {
    message: truncateNonEmptyText(messageCandidate, 600) || fallbackMessage,
    name: truncateNonEmptyText(nameCandidate, nameMaxLength),
    code:
      typeof codeCandidate === "string" || typeof codeCandidate === "number"
        ? String(codeCandidate).slice(0, codeMaxLength)
        : null,
    stack: truncateNonEmptyText(stackCandidate, stackMaxLength),
    data: sanitizeStructuredValue(dataValue),
  };
}
